import { createApi } from '@reduxjs/toolkit/query/react'
import { BasePageRequest, BasePageResponse, BaseRequest, BaseResponse } from '../models'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'
import { paginate } from '../utils'
import { env } from '../../common/util'

// Define a service using a base URL and expected endpoints
const _BASE_URL = import.meta.env.REACT_APP_AI_BASE_URL!

const internalMeetingSystemTemplateId = { dev: 'a01uu6an4iqbzljz', staging: '1eps9ip0zpke8ov3', prod: 'cjlinbnchayis0kg' }

export const INTERNAL_MEETING_SYSTEM_TEMPLATE_ID = internalMeetingSystemTemplateId[env]

// There is no Client Meeting system template on staging
const clientMeetingSystemTemplateId = { dev: 'a0dtbb6kvlropd90', staging: undefined, prod: 'a0dtbb6kvlropd90' }

export const CLIENT_MEETING_SYSTEM_TEMPLATE_ID = clientMeetingSystemTemplateId[env]

const generalClientCheckInSystemTemplateId = { dev: 'b6588h4yixo4reju', staging: '4oacf02mlw3jadhj', prod: '1yxfmx1grg98vxig' }

export const GENERAL_CLIENT_CHECK_IN_SYSTEM_TEMPLATE_ID = generalClientCheckInSystemTemplateId[env]

export interface GetMeetingTemplatesRequest extends BasePageRequest {
  includeInactive?: boolean
  excludeDeprecated?: boolean
}

export interface GetAllMeetingTemplatesRequest extends BaseRequest {
  includeInactive?: boolean
  excludeDeprecated?: boolean
}

export interface GetMeetingTemplateByIdRequest extends BaseRequest {
  meetingTemplateId: string
}

export interface GetDefaultMeetingTemplateRequest extends BaseRequest {
  userId: string | null
}

export interface SetUserDefaultMeetingTemplatePayload {
  meetingTemplateId: string | null
}

export interface SetUserDefaultMeetingTemplateRequest extends BaseRequest {
  payload: SetUserDefaultMeetingTemplatePayload
}

export interface MeetingTemplatePreset {
  aiFeaturePresetId: string
  aiFeatureId?: string
  metadata: string
}

export interface MeetingTemplateDetectionConfig {
  description: string
  averageMeetingTime?: string
  meetingName?: string
}

export interface MeetingTemplate {
  id: string
  name: string
  presets: MeetingTemplatePreset[]
  formPresets: MeetingTemplatePreset[]
  isActive: boolean
  isDefault: boolean
  isPrivate: boolean
  detectionConfig: MeetingTemplateDetectionConfig
  fromSystemMeetingTemplate: boolean
  systemMeetingTemplateId?: string
  metadata: string
  isDeprecated: boolean
  newMeetingTemplateId?: string
  label?: string
  isManagedByEnterprise: boolean
}

export interface DefaultMeetingTemplate extends MeetingTemplate {
  isUserDefault: boolean
}

type MeetingTemplateIdentity = Pick<MeetingTemplate, 'id' | 'systemMeetingTemplateId'>

const isFromSystemMeetingTemplate = (meetingTemplate: MeetingTemplateIdentity | null | undefined, systemMeetingTemplateId: string | undefined): boolean =>
  !!systemMeetingTemplateId &&
  !!meetingTemplate &&
  (meetingTemplate.id === systemMeetingTemplateId || meetingTemplate.systemMeetingTemplateId === systemMeetingTemplateId)

export const isInternalMeetingTemplate = (meetingTemplate?: MeetingTemplateIdentity | null): boolean =>
  isFromSystemMeetingTemplate(meetingTemplate, INTERNAL_MEETING_SYSTEM_TEMPLATE_ID)

export const isClientMeetingTemplate = (meetingTemplate?: MeetingTemplateIdentity | null): boolean =>
  isFromSystemMeetingTemplate(meetingTemplate, CLIENT_MEETING_SYSTEM_TEMPLATE_ID) ||
  isFromSystemMeetingTemplate(meetingTemplate, GENERAL_CLIENT_CHECK_IN_SYSTEM_TEMPLATE_ID)

export interface CreateMeetingTemplatePayload {
  name: string
  isActive?: boolean
  isDefault?: boolean
  presets: MeetingTemplatePreset[]
  formPresets: MeetingTemplatePreset[]
  detectionConfig: MeetingTemplateDetectionConfig
}

export interface CreateMeetingTemplateRequest extends BaseRequest {
  payload: CreateMeetingTemplatePayload
}

export interface UpdateMeetingTemplatePayload {
  name?: string
  isActive?: boolean
  isDefault?: boolean
  isPrivate?: boolean
  presets?: MeetingTemplatePreset[]
  formPresets?: MeetingTemplatePreset[]
  detectionConfig?: MeetingTemplateDetectionConfig
  metadata?: string
  changePastMeetings?: boolean
}

export interface UpdateMeetingTemplateRequest extends BaseRequest {
  meetingTemplateId: string
  payload: UpdateMeetingTemplatePayload
}

export interface DeleteMeetingTemplateRequest extends BaseRequest {
  meetingTemplateId: string
}

export type AgentType = 'meetingPreparation' | 'meetingSummary'

export interface MeetingTypeForSession {
  id: string
  name: string
  deprecated: boolean
  label: string | null
  newMeetingTemplateId: string | null
}

export interface GetMeetingTemplatesBySessionRequest extends BaseRequest {
  roomId: string
  sessionId: string
  agentType?: AgentType
}

export const meetingTemplateApi = createApi({
  reducerPath: 'meetingTemplateApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  tagTypes: ['MeetingTemplate'],
  endpoints: (builder) => ({
    getMeetingTemplates: builder.query<BasePageResponse<MeetingTemplate[]>, GetMeetingTemplatesRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/meeting-templates`,
          params: {
            page: request.pageRequest.page,
            size: request.pageRequest.size,
            includeInactive: request.includeInactive,
            excludeDeprecated: request.excludeDeprecated,
          },
        }
      },
      providesTags: ['MeetingTemplate'],
    }),
    getAllMeetingTemplates: builder.query<BaseResponse<MeetingTemplate[]>, GetAllMeetingTemplatesRequest>({
      async queryFn(request, _queryApi, _extraOptions, fetchWithBQ) {
        return await paginate<MeetingTemplate>(
          {
            url: `/accounts/${request.accountId}/meeting-templates`,
            params: {
              includeInactive: request.includeInactive,
              excludeDeprecated: request.excludeDeprecated,
            },
          },
          fetchWithBQ,
        )
      },
      providesTags: ['MeetingTemplate'],
    }),
    getMeetingTemplateById: builder.query<BaseResponse<MeetingTemplate>, GetMeetingTemplateByIdRequest>({
      query: (request) => `/accounts/${request.accountId}/meeting-templates/${request.meetingTemplateId}`,
      providesTags: ['MeetingTemplate'],
    }),
    getDefaultMeetingTemplate: builder.query<DefaultMeetingTemplate, GetDefaultMeetingTemplateRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/meeting-templates/default`,
          params: { userId: request.userId },
        }
      },
      transformResponse: (response: BaseResponse<DefaultMeetingTemplate>) => {
        return response.data
      },
      providesTags: ['MeetingTemplate'],
    }),
    createMeetingTemplate: builder.mutation<MeetingTemplate, CreateMeetingTemplateRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/meeting-templates/`,
        method: 'POST',
        body: request.payload,
      }),
      transformResponse: (response: BaseResponse<MeetingTemplate>) => {
        return response.data
      },
      invalidatesTags: ['MeetingTemplate'],
    }),
    updateMeetingTemplate: builder.mutation<void, UpdateMeetingTemplateRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/meeting-templates/${request.meetingTemplateId}`,
        method: 'PATCH',
        body: request.payload,
      }),
      invalidatesTags: ['MeetingTemplate'],
    }),
    deleteMeetingTemplate: builder.mutation<void, DeleteMeetingTemplateRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/meeting-templates/${request.meetingTemplateId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['MeetingTemplate'],
    }),
    setUserDefaultMeetingTemplate: builder.mutation<BaseResponse<DefaultMeetingTemplate>, SetUserDefaultMeetingTemplateRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/meeting-templates/set-user-default`,
        method: 'POST',
        body: request.payload,
      }),
      invalidatesTags: ['MeetingTemplate'],
    }),
    checkMigrationNeeded: builder.query<boolean, BaseRequest>({
      query: (request) => `/accounts/${request.accountId}/meeting-templates/migration`,
      transformResponse: (response: BaseResponse<boolean>) => response.data,
      providesTags: ['MeetingTemplate'],
    }),
    migrateMeetingTemplates: builder.mutation<{ migrated: number; mapping: Record<string, string> }, BaseRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/meeting-templates/migration`,
        method: 'POST',
      }),
      transformResponse: (response: BaseResponse<{ migrated: number; mapping: Record<string, string> }>) => response.data,
      invalidatesTags: ['MeetingTemplate'],
    }),
    getMeetingTemplatesBySession: builder.query<MeetingTypeForSession[], GetMeetingTemplatesBySessionRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/rooms/${request.roomId}/sessions/${request.sessionId}/meeting-templates`,
        params: request.agentType ? { type: request.agentType } : undefined,
      }),
      transformResponse: (response: BaseResponse<MeetingTypeForSession[]>) => response.data,
    }),
    getAgendaAndScorecardBackup: builder.mutation<Blob, BaseRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/meeting-templates/ip-config-backup`,
        responseHandler: (response) => response.blob(),
      }),
    }),
  }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetMeetingTemplatesQuery,
  useLazyGetMeetingTemplatesQuery,
  useGetAllMeetingTemplatesQuery,
  useGetDefaultMeetingTemplateQuery,
  useLazyGetDefaultMeetingTemplateQuery,
  useGetMeetingTemplateByIdQuery,
  useCreateMeetingTemplateMutation,
  useUpdateMeetingTemplateMutation,
  useDeleteMeetingTemplateMutation,
  useSetUserDefaultMeetingTemplateMutation,
  useCheckMigrationNeededQuery,
  useMigrateMeetingTemplatesMutation,
  useGetMeetingTemplatesBySessionQuery,
  useGetAgendaAndScorecardBackupMutation,
} = meetingTemplateApi
